/**
 * Shared liveness thresholds (twin slice 1, contract C0; plan §4.1 items 2
 * and 6).
 *
 * ONE set of numbers for every "is this still reporting" question the
 * twin, the 2D Overview, the attention list and the lift columns ask, so
 * a robot that reads "quiet" on the map can never read "live" in a card.
 *
 * WHAT THE AGE IS. `ageMs` is measured on THIS DEVICE's clock, from this
 * page's own receipt of a record whose stamp advanced. It is never
 * `Date.now() - updated_at_ms`: that stamp is the worker's (or the
 * adapter's) clock, and any skew between the two would make a dead feed
 * look live or a live one dead. Producing the age is the receipts module's
 * job (W3) and the lift reading's (W6), not this file's.
 */

/** A record older than this is "quiet": drawn dimmer, never solid. */
export const QUIET_AFTER_MS = 10_000;

/** A record this old or older is "lost": a ghost at the last real pose,
 *  "No signal" on a lift car. */
export const LOST_AFTER_MS = 30_000;

/** How long after a lift ride a robot's position reads as settling
 *  ("position updating after lift") rather than as a fresh fix. */
export const AFTER_RIDE_MS = 30_000;

export type Liveness = 'live' | 'quiet' | 'lost';

/**
 * `live` below 10 s, `quiet` from 10 s up to 30 s, `lost` from 30 s.
 *
 * Only an age positively inside a range earns `live` or `quiet`; anything
 * else -- negative, NaN, infinite -- is `lost`. None of those can come
 * from one monotonic clock, so each means the caller's clock stepped or
 * the input is broken, and the honest reading of "can't tell how old" is
 * the one that never looks live.
 */
export function classify(ageMs: number): Liveness {
  if (ageMs >= 0 && ageMs < QUIET_AFTER_MS) return 'live';
  if (ageMs >= QUIET_AFTER_MS && ageMs < LOST_AFTER_MS) return 'quiet';
  return 'lost';
}

/**
 * Milliseconds until `classify` would change for this record with no new
 * receipt, for a boundary timer. Null once `lost`: only a new receipt
 * changes that.
 */
export function msUntilNextBoundary(ageMs: number): number | null {
  const now = classify(ageMs);
  if (now === 'lost') return null;
  return (now === 'live' ? QUIET_AFTER_MS : LOST_AFTER_MS) - ageMs;
}
